/**
 * Shared load helpers — deadlines, safe payload shaping, empty/shell batches.
 * No React setters here (keeps this free of DataContext coupling).
 */

import { resolveWorkerSessionsForBusiness } from '../../utils/workerSessionsResolve';

/** Per-field deadline — one hung reader must not block shell unlock. */
export const ENTITY_FIELD_READ_DEADLINE_MS = 12000;
/** Hard cap for Phase-1 entity reads before unlocking with whatever succeeded. */
export const PHASE1_ENTITY_READ_BUDGET_MS = 40000;
export const ENTITY_HYDRATE_AWAIT_MS = 25000;
export const ENTITY_COUNT_DEADLINE_MS = 8000;
export const ENTITY_RESYNC_DEADLINE_MS = 20000;
export const EMERGENCY_SHELL_LOAD_MS = 15000;
export const EMERGENCY_FIELD_READ_MS = 8000;
/** Eager (non-lazy) full entity read budget. */
export const EAGER_ENTITY_READ_BUDGET_MS = 60000;
/**
 * Max simultaneous entity field reads (Phase-2 / eager).
 * Uncapped Promise.all flooded IDB while migration `'rw'` held all entity stores.
 */
export const ENTITY_FIELD_READ_CONCURRENCY = 4;
/** Phase-2 waits for entity writers (hydrate/migration) before starting deferred reads. */
export const PHASE2_WRITER_WAIT_MS = 18000;

const LOG_PREFIX = '[businessLoad]';

/**
 * Soft deadline via Promise.race — does NOT cancel the underlying work.
 *
 * IndexedDB/Dexie: after timeout the IDB request/transaction may still run and
 * keep holding locks ("zombie"). Callers that start heavy IDB work should
 * coordinate via entityStoreActivityState instead of relying on cancellation.
 *
 * @template T
 * @param {Promise<T>} promise
 * @param {number} ms
 * @param {string} [label]
 * @returns {Promise<T>}
 */
export function withDeadline(promise, ms, label = 'operation') {
  if (!Number.isFinite(ms) || ms <= 0) return promise;
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      reject(new Error(`${LOG_PREFIX} ${label} timed out after ${ms}ms`));
    }, ms);
  });
  return Promise.race([promise, timeout]).finally(() => {
    if (timer != null) clearTimeout(timer);
  });
}

/** Never throw — log and return fallback. */
export async function runSoft(label, fn, fallback = undefined) {
  try {
    return await fn();
  } catch (err) {
    console.warn(`${LOG_PREFIX} ${label}:`, err?.message || err);
    return fallback;
  }
}

export function warnLoad(label, err) {
  console.warn(`${LOG_PREFIX} ${label}:`, err?.message || err);
}

export function asArray(raw) {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') return Object.values(raw);
  return [];
}

export function asObject(raw, fallback = {}) {
  return raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : fallback;
}

export function countLotEntries(lots) {
  if (Array.isArray(lots)) return lots.length;
  if (lots && typeof lots === 'object') return Object.keys(lots).length;
  return 0;
}

export function countProducts(batchData) {
  return batchData?.products?.length ?? 0;
}

export function countPurchases(batchData) {
  return batchData?.purchases?.length ?? 0;
}

export function buildEmptyBusinessBatch() {
  return {
    products: [],
    suppliers: [],
    purchases: [],
    orders: [],
    customers: [],
    sales: [],
    expiredProducts: [],
    invoices: [],
    invoicePayments: [],
    supermarketPayments: [],
    supermarketInvoices: [],
    supplierDebts: [],
    customerDebts: [],
    customerInvoices: [],
    expenses: [],
    restaurantDishes: [],
    stockConsumptions: [],
    restaurantTables: [],
    openTickets: [],
    drafts: [],
    recipes: [],
    productionOrders: [],
    lots: [],
    stockData: {},
    workerSessions: [],
    notifications: [],
    reports: {},
    stockLocations: [],
    persistentStockData: [],
    offlineQueue: [],
    businessInfo: null,
    userInfo: null,
    metadata: {},
    discrepancyManagement: {
      selfDeviations: [],
      handoverDeviations: [],
      workerTolerances: {},
      pendingReviews: [],
      auditTrail: [],
      notifications: [],
      toleranceSystemEnabled: false
    }
  };
}

function normalizeDiscrepancyManagement(raw) {
  const discrepancyData = asObject(raw);
  return {
    selfDeviations: asArray(discrepancyData.selfDeviations),
    handoverDeviations: asArray(discrepancyData.handoverDeviations),
    workerTolerances: asObject(discrepancyData.workerTolerances),
    pendingReviews: asArray(discrepancyData.pendingReviews),
    auditTrail: asArray(discrepancyData.auditTrail),
    notifications: asArray(discrepancyData.notifications),
    toleranceSystemEnabled: Boolean(discrepancyData.toleranceSystemEnabled)
  };
}

function resolveWorkerSessionsSafe(businessId, sessions) {
  try {
    return resolveWorkerSessionsForBusiness(businessId, sessions);
  } catch (err) {
    warnLoad('workerSessions resolve', err);
    return sessions;
  }
}

/** Shape corrupt/partial payloads into a safe React batch (never throws on bad types). */
export function buildBatchDataFromParsed(parsedData, businessId) {
  const parsed = parsedData && typeof parsedData === 'object' ? parsedData : {};
  return {
    products: asArray(parsed.products),
    suppliers: asArray(parsed.suppliers),
    purchases: asArray(parsed.purchases),
    orders: asArray(parsed.orders),
    customers: asArray(parsed.customers).filter(
      (customer) => customer && customer.customerId && typeof customer === 'object'
    ),
    sales: asArray(parsed.sales),
    expiredProducts: asArray(parsed.expiredProducts),
    invoices: asArray(parsed.invoices),
    invoicePayments: asArray(parsed.invoicePayments),
    supermarketPayments: asArray(parsed.supermarketPayments),
    supermarketInvoices: asArray(parsed.supermarketInvoices),
    supplierDebts: asArray(parsed.supplierDebts),
    customerDebts: asArray(parsed.customerDebts),
    customerInvoices: asArray(parsed.customerInvoices),
    expenses: asArray(parsed.expenses),
    restaurantDishes: asArray(parsed.restaurantDishes),
    stockConsumptions: asArray(parsed.stockConsumptions),
    restaurantTables: asArray(parsed.restaurantTables),
    openTickets: asArray(parsed.openTickets),
    drafts: asArray(parsed.drafts),
    recipes: asArray(parsed.recipes),
    productionOrders: asArray(parsed.productionOrders),
    lots: asArray(parsed.lots),
    stockData: asObject(parsed.stockData),
    workerSessions: resolveWorkerSessionsSafe(businessId, asArray(parsed.workerSessions)),
    notifications: asArray(parsed.notifications),
    reports: asObject(parsed.reports),
    stockLocations: asArray(parsed.stockLocations),
    persistentStockData: asArray(parsed.persistentStockData),
    offlineQueue: asArray(parsed.offlineQueue),
    businessInfo: parsed.businessInfo || null,
    userInfo: parsed.userInfo || null,
    metadata: asObject(parsed.metadata),
    discrepancyManagement: normalizeDiscrepancyManagement(parsed.discrepancyManagement)
  };
}

/**
 * Recovery threshold: any POS surface is better than empty defaults.
 * (products alone OR lots/purchases)
 */
export function batchHasUsablePosShell(batchData) {
  if (!batchData) return false;
  return (
    countProducts(batchData) > 0 ||
    countLotEntries(batchData.lots) > 0 ||
    countPurchases(batchData) > 0
  );
}

/**
 * Fast-blob / core POS: catalog + inventory evidence (stricter than usable shell).
 */
export function hasBatchCorePosData(batchData) {
  if (!batchData) return false;
  if (countProducts(batchData) === 0) return false;
  return countLotEntries(batchData.lots) > 0 || countPurchases(batchData) > 0;
}
